import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {
  choiceHealthCareApi,
  downloadStatusHealthCareApi,
  HdAnalysisTypes,
  HealthCareChoiceRequest,
  HealthCareDownloadRequest,
  HealthCareSearchRequest,
  SearchedHealthCare,
  searchHealthCareAnalysisApi,
} from 'api/healthCareApi'
import {getDefaultPeriodString, periodStringToDate} from 'helpers/dateHelper'
import {RootState} from 'store'

export const searchHealthCare = createAsyncThunk(
  'api/healthCare/search',
  async (payload: HealthCareSearchRequest, {rejectWithValue}) => {
    try {
      const response = await searchHealthCareAnalysisApi(payload)
      return response.data
    } catch (err) {
      return rejectWithValue(err.message)
    }
  },
)

export const choiceHealthCare = createAsyncThunk(
  'api/healthCare/choice',
  async (payload: HealthCareChoiceRequest, {rejectWithValue}) => {
    try {
      const response = await choiceHealthCareApi(payload)
      return response.data
    } catch (err) {
      return rejectWithValue(err.message)
    }
  },
)

export const downloadHealthCare = createAsyncThunk(
  'api/healthCare/download',
  async (payload: HealthCareDownloadRequest, {rejectWithValue}) => {
    try {
      const response = await downloadStatusHealthCareApi(payload)
      return response.list
    } catch (err) {
      return rejectWithValue(err.message)
    }
  },
)

interface HdAnalysisTypesStore {
  id: number
  list: HdAnalysisTypes[]
}

export interface HealthCareStateWhiteList {
  /**
   * State for GET /api/healthcare/v1/analysis
   */
  loading: boolean
  error: any
  /**
   * State for PUT /api/healthcare/v1/analysis/{id}/choice
   */
  loadingChoice: boolean
  loadingDownload: boolean
  errorChoice: any
  errorDownload: any
  items: SearchedHealthCare[]
  startDate: string
  endDate: string
  hcId: string
  patientName: string
  page: number
  size: number
  lang: string
  pagingInfo: {
    totalPages: number
    totalElements: number
  }
  hdAnalysisTypes: HdAnalysisTypesStore[] | null
  birth: string
  patientVisitNo: string
  patientChartNo: string
  downloaded: boolean | string
}

const initialState: HealthCareStateWhiteList = {
  loading: false,
  loadingChoice: false,
  loadingDownload: false,
  error: null,
  errorChoice: null,
  errorDownload: null,
  items: [],
  startDate: getDefaultPeriodString().startDate, // TODO: 시간 관련 나래와 협의
  endDate: getDefaultPeriodString().endDate, // TODO: 시간 관련 나래와 협의
  hcId: '',
  patientName: '',
  page: 0,
  size: 10,
  lang: 'ko',
  pagingInfo: {
    totalPages: 1,
    totalElements: 0,
  },
  hdAnalysisTypes: null,
  birth: '',
  patientVisitNo: '',
  patientChartNo: '',
  downloaded: '',
}

const slice = createSlice({
  name: 'healthCareWhiteListSearchSlice',
  initialState,
  reducers: {
    setHdAnalysisTypes(state, action: PayloadAction<HdAnalysisTypesStore[]>) {
      state.hdAnalysisTypes = action.payload
    },
  },
  extraReducers: (builder) => {
    // GET /api/healthcare/v1/analysis
    builder.addCase(searchHealthCare.pending, (state, action) => {
      state.loading = true
      state.error = null

      // TODO: Implement Reducers
      const {
        startDate,
        endDate,
        hcId,
        patientName,
        page,
        size,
        birth,
        patientVisitNo,
        patientChartNo,
        downloaded,
      } = action.meta.arg
      state.startDate = startDate || getDefaultPeriodString().startDate
      state.endDate = endDate || getDefaultPeriodString().endDate
      state.patientName = patientName || ''
      state.patientName = patientName || ''
      state.page = page || 0
      state.size = size || 10
      state.birth = birth || ''
      state.patientVisitNo = patientVisitNo || ''
      state.patientChartNo = patientChartNo || ''
      state.downloaded = downloaded || ''
    })
    builder.addCase(searchHealthCare.fulfilled, (state, action) => {
      state.loading = false
      const {
        content: items,
        pageable: {pageNumber: page},
        size,
        totalPages,
        totalElements,
      } = action.payload
      state.items = items
      state.page = page
      state.size = size
      state.pagingInfo.totalElements = totalElements
      state.pagingInfo.totalPages = totalPages
    })
    builder.addCase(searchHealthCare.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
    // PUT /api/healthcare/v1/analysis/{id}/choice
    builder.addCase(choiceHealthCare.pending, (state) => {
      state.loadingChoice = true
      state.errorChoice = null
    })
    builder.addCase(choiceHealthCare.fulfilled, (state) => {
      state.loadingChoice = false
    })
    builder.addCase(choiceHealthCare.rejected, (state, action) => {
      state.loadingChoice = false
      state.errorChoice = action.payload
    })

    // PUT /api/healthcare/v1/analysis/any/download-status
    builder.addCase(downloadHealthCare.pending, (state) => {
      state.loadingDownload = true
      state.errorDownload = null
    })
    builder.addCase(downloadHealthCare.fulfilled, (state) => {
      state.loadingDownload = false
      state.errorDownload = null
    })
    builder.addCase(downloadHealthCare.rejected, (state, action) => {
      state.loadingDownload = false
      state.errorDownload = action.payload
    })
  },
})

export default slice.reducer

export const {setHdAnalysisTypes} = slice.actions

export const selectLoading = (state: RootState) =>
  state.healthCareWhiteList.loading
export const selectError = (state: RootState) => state.healthCareWhiteList.error
export const selectLoadingChoice = (state: RootState) =>
  state.healthCareWhiteList.loadingChoice
export const selectErrorChoice = (state: RootState) =>
  state.healthCareWhiteList.errorChoice
export const selectLoadingDownload = (state: RootState) =>
  state.healthCareWhiteList.loadingDownload
export const selectErrorDownload = (state: RootState) =>
  state.healthCareWhiteList.errorDownload
export const selectItems = (state: RootState) => state.healthCareWhiteList.items
export const selectPeriod = (state: RootState) =>
  periodStringToDate({
    startDate: state.healthCareWhiteList.startDate,
    endDate: state.healthCareWhiteList.endDate,
  })
export const selectSearch = (state: RootState) => ({
  patientName: state.healthCareWhiteList.patientName,
})
export const selectPaging = (state: RootState) => ({
  page: state.healthCareWhiteList.page,
  size: state.healthCareWhiteList.size,
})
export const selectPagingInfo = (state: RootState) =>
  state.healthCareWhiteList.pagingInfo
export const selectQuery = (state: RootState) => ({
  startDate: state.healthCareWhiteList.startDate,
  endDate: state.healthCareWhiteList.endDate,
  patientName: state.healthCareWhiteList.patientName,
  hcId: state.healthCareWhiteList.hcId,
  size: state.healthCareWhiteList.size,
  page: state.healthCareWhiteList.page,
  lang: state.healthCareWhiteList.lang,
  birth: state.healthCareWhiteList.birth,
  patientVisitNo: state.healthCareWhiteList.patientVisitNo,
  patientChartNo: state.healthCareWhiteList.patientChartNo,
  downloaded: state.healthCareWhiteList.downloaded,
})

export const selectHdAnalysisTypes = (state: RootState) =>
  state.healthCareWhiteList.hdAnalysisTypes
